/**
 * Ads / GA4 conversion tracking.
 *
 * Production HTML embeds GTM (`GTM-5B5SJQ2R`, same container as www.orsane.app).
 * Events are pushed onto `dataLayer` for that container. This module:
 *   - injects GTM (unless `VITE_GTM_ID` is set to an empty string)
 *   - installs a `gtag()` stub so events queue before the container is ready
 *   - optionally configs a measurement / Ads id for linker + enhanced conversions
 *   - emits the GA4 recommended `sign_up` event exactly once on account creation
 *   - emits the GA4 recommended `purchase` event exactly once on trial → paid
 *
 * All GTM / gtag / dataLayer work is fire-and-forget: ad blockers must never
 * throw into signup or checkout success. See `analytics.README.md`.
 */

import { afterSuccessTrack, emitPlannedEventSafely } from "./analyticsSafe";
import { attachPurchaseMetaEventId, attachSignUpMetaEventId } from "./metaDataLayer";
import {
    classifyPaidSubscription,
    consumePendingPaidConversion,
    decidePaidConversionFire,
    emitPurchaseSafely,
    extractCheckoutPlan,
    extractCheckoutSubscriptionId,
    extractCheckoutValue,
    hasFiredPaidConversion,
    markPaidConversionFired,
    markPendingPaidConversion,
    PAID_CONVERSION_POLL_ATTEMPTS,
    PAID_CONVERSION_POLL_INTERVAL_MS,
    type PendingPaidConversion,
    PURCHASE_EVENT,
    peekPendingPaidConversion,
    pendingMatchesOrganization,
    planPurchaseEmission,
    resolvePaidSubscriptionId,
    type SubscriptionSnapshot,
} from "./paidConversionTracking";
import {
    consumePendingSignUp,
    decideGoogleOAuthReturnFire,
    decideSignUpFire,
    hasFiredSignUp,
    markPendingSignUp,
    markSignUpFired,
    peekPendingSignUp,
    planSignUpEmission,
    SIGN_UP_EVENT,
    type SignUpEventParams,
    type SignUpMethod,
} from "./signUpTracking";

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown> | IArguments>;
        gtag?: (...args: unknown[]) => void;
    }
}

// Default production container. Override with VITE_GTM_ID (set it to an empty
// string to disable GTM entirely, e.g. in local dev / preview builds).
const DEFAULT_GTM_ID = "GTM-5B5SJQ2R";
const GTM_ID = (import.meta.env.VITE_GTM_ID as string | undefined) ?? DEFAULT_GTM_ID;

// GA4 measurement id is not hardcoded: Admin property_details does not return G-…
// Set VITE_GA_MEASUREMENT_ID from the GA4 data stream (or GTM Google tag).
const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ?? "";

const GOOGLE_ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) ?? "";

const DEFAULT_LINKER_DOMAINS = ["orsane.app", "my.orsane.app"];
const rawLinker = import.meta.env.VITE_GA_LINKER_DOMAINS as string | undefined;
const LINKER_DOMAINS = rawLinker
    ? rawLinker
          .split(",")
          .map((domain) => domain.trim())
          .filter(Boolean)
    : DEFAULT_LINKER_DOMAINS;

let initialized = false;

function getGtag(): ((...args: unknown[]) => void) | undefined {
    try {
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== "function") {
            // gtag.js / GTM consume the Arguments object, not a rest-parameter array.
            window.gtag = function gtag() {
                window.dataLayer!.push(arguments);
            };
        }
        return window.gtag;
    } catch {
        return undefined;
    }
}

function isGtmAlreadyInjected(): boolean {
    try {
        return Boolean(document.querySelector('script[src*="googletagmanager.com/gtm.js"]'));
    } catch {
        return false;
    }
}

/** Throws so `emitPlannedEventSafely` can report a blocked push (do not mark fired). */
function pushDataLayerOrThrow(event: Record<string, unknown>): void {
    if (typeof window === "undefined") {
        throw new Error("dataLayer unavailable");
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

function emitDeps() {
    return {
        pushDataLayer: pushDataLayerOrThrow,
        gtag: getGtag(),
    };
}

function emitSignUp(params: SignUpEventParams, userId?: string | null): boolean {
    const plan = planSignUpEmission({
        gtmId: GTM_ID,
        gaMeasurementId: GA_MEASUREMENT_ID,
        params,
    });
    // GTM Meta tag reads `meta_event_id`. gtag params stay the GA4 payload.
    return emitPlannedEventSafely(
        emitDeps(),
        { ...plan, dataLayer: attachSignUpMetaEventId(plan.dataLayer, userId) },
        { name: SIGN_UP_EVENT, params }
    );
}

function sessionStorageOrNull(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

let signUpFiredInMemory = false;
const paidFiredInMemory = new Set<string>();

/**
 * Injects the GTM container (and optional gtag.js) and bootstraps the data layer.
 * Safe to call multiple times; only runs once.
 */
export function initAnalytics(): void {
    try {
        if (initialized || typeof window === "undefined") {
            return;
        }
        initialized = true;

        window.dataLayer = window.dataLayer || [];
        const gtag = getGtag();

        if (GA_MEASUREMENT_ID) {
            // When GTM is on, its Google tag already sends page_view. A second config
            // is still useful for linker + enhanced conversions; suppress the extra hit.
            if (!GTM_ID) {
                const script = document.createElement("script");
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
                document.head.appendChild(script);
            }
            gtag?.("js", new Date());
            gtag?.("config", GA_MEASUREMENT_ID, {
                send_page_view: !GTM_ID,
                allow_enhanced_conversions: true,
                linker: { domains: LINKER_DOMAINS, accept_incoming: true },
            });
        } else {
            gtag?.("set", "linker", { domains: LINKER_DOMAINS, accept_incoming: true });
        }

        if (GOOGLE_ADS_ID) {
            gtag?.("config", GOOGLE_ADS_ID, {
                allow_enhanced_conversions: true,
            });
        }

        if (!GTM_ID) {
            console.info("GTM container id is empty, skipping Google Tag Manager initialization");
            return;
        }

        // Production HTML already embeds the same snippet (head + noscript). Don't load twice.
        if (isGtmAlreadyInjected()) {
            return;
        }

        window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
        document.head.appendChild(script);
    } catch {
        // Ad blockers often throw on gtm.js / gtag.js injection. Boot the app anyway.
    }
}

/** Low-level helper: push a raw event object onto the GTM data layer. Never throws. */
export function pushDataLayer(event: Record<string, unknown>): void {
    try {
        if (typeof window === "undefined") {
            return;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(event);
    } catch {
        // ad blocker stub / frozen dataLayer
    }
}

/**
 * GA4 recommended `sign_up` event. Fires at most once per browser tab session.
 * Call only after the account actually exists (not validation / Turnstile failure).
 * `user_data.email` is plaintext; gtag hashes it for enhanced conversions.
 */
export function trackSignUp(params: { method: SignUpMethod; email?: string | null; userId?: string | null }): void {
    afterSuccessTrack(() => {
        if (typeof window === "undefined") {
            return;
        }
        const storage = sessionStorageOrNull();
        const alreadyFired = signUpFiredInMemory || (storage ? hasFiredSignUp(storage) : false);
        const decision = decideSignUpFire({
            outcome: "success",
            method: params.method,
            email: params.email,
            alreadyFired,
        });
        if (!decision.fire) {
            return;
        }
        if (!emitSignUp(decision.event.params, params.userId)) {
            return;
        }
        signUpFiredInMemory = true;
        if (storage) {
            markSignUpFired(storage);
        }
    });
}

/** Remember that the user started Google OAuth from /signup so we can fire after the return. */
export function markPendingGoogleSignUp(email?: string): void {
    afterSuccessTrack(() => {
        if (typeof window === "undefined") {
            return;
        }
        const storage = sessionStorageOrNull();
        if (!storage) {
            return;
        }
        markPendingSignUp(storage, { method: "google", email });
    });
}

/**
 * After Google OAuth lands back on this origin, fire `sign_up` once for a new
 * account. No-ops when nothing is pending, the account is old, or we already fired.
 */
export function flushPendingSignUp(user?: {
    id?: string | null;
    email?: string | null;
    createdAt?: Date | string | null;
}): void {
    afterSuccessTrack(() => {
        if (typeof window === "undefined") {
            return;
        }
        const storage = sessionStorageOrNull();
        const pending = storage ? peekPendingSignUp(storage) : null;
        const alreadyFired = signUpFiredInMemory || (storage ? hasFiredSignUp(storage) : false);
        const decision = decideGoogleOAuthReturnFire({
            hasPendingSignUp: pending !== null,
            alreadyFired,
            email: user?.email ?? pending?.email,
            userCreatedAt: user?.createdAt,
        });
        if (!decision.fire) {
            if (storage) {
                consumePendingSignUp(storage);
            }
            return;
        }
        if (!emitSignUp(decision.event.params, user?.id)) {
            return;
        }
        signUpFiredInMemory = true;
        if (storage) {
            markSignUpFired(storage);
            consumePendingSignUp(storage);
        }
    });
}

/**
 * Conversion event: a trial started (an organization / account was created).
 *
 * Wire a "Custom Event" trigger on `trial_signup` in GTM and attach the
 * corresponding Google Ads conversion action.
 */
export function trackTrialSignup(params: { organizationId?: string; method?: "email" | "google" } = {}): void {
    afterSuccessTrack(() => {
        pushDataLayer({
            event: "trial_signup",
            organization_id: params.organizationId,
            signup_method: params.method,
        });
    });
}

function emitPaidPurchase(params: Parameters<typeof emitPurchaseSafely>[2]): boolean {
    const plan = planPurchaseEmission({
        gtmId: GTM_ID,
        gaMeasurementId: GA_MEASUREMENT_ID,
        params,
    });
    // `value` and `currency` are already on the GA4 purchase push. `meta_event_id`
    // is only added there so the GTM Purchase tag can dedupe with CAPI.
    return emitPurchaseSafely(
        emitDeps(),
        { ...plan, dataLayerEvents: attachPurchaseMetaEventId(plan.dataLayerEvents, params.subscription_id) },
        params
    );
}

function tryFirePaidConversion(input: {
    outcome: Parameters<typeof decidePaidConversionFire>[0]["outcome"];
    paddleSubscriptionId?: string | null;
    pending?: PendingPaidConversion | null;
    email?: string | null;
}): boolean {
    const id = input.paddleSubscriptionId ?? undefined;
    const storage = typeof window === "undefined" ? null : sessionStorageOrNull();
    const alreadyFired = Boolean(id && (paidFiredInMemory.has(id) || hasFiredPaidConversion(storage, id)));
    const decision = decidePaidConversionFire({
        outcome: input.outcome,
        paddleSubscriptionId: id,
        alreadyFired,
        value: input.pending?.value,
        currency: input.pending?.currency,
        transactionId: input.pending?.transactionId,
        organizationId: input.pending?.organizationId,
        quantity: input.pending?.quantity,
        plan: input.pending?.plan,
        email: input.email ?? input.pending?.email,
    });
    if (!decision.fire) {
        return false;
    }
    const emitted = emitPaidPurchase(decision.event.params);
    if (!emitted) {
        return false;
    }
    paidFiredInMemory.add(decision.event.params.subscription_id);
    markPaidConversionFired(storage, decision.event.params.subscription_id);
    consumePendingPaidConversion(storage);
    return true;
}

/**
 * Conversion event: trial → paid once a real Paddle `sub_…` id is known.
 *
 * Do not call this with the `"temp"` placeholder from `subscription.create`.
 * Prefer `confirmPaidConversionFromCheckout` / `flushPendingPaidConversion`.
 */
export function trackPaidConversion(params: {
    paddleSubscriptionId?: string | null;
    transactionId?: string;
    value?: number;
    currency?: string;
    organizationId?: string;
    quantity?: number;
    plan?: string;
    email?: string | null;
}): void {
    afterSuccessTrack(() => {
        tryFirePaidConversion({
            outcome: classifyPaidSubscription({
                paddleSubscriptionId: params.paddleSubscriptionId,
                isActive: true,
                status: "active",
            }),
            paddleSubscriptionId: params.paddleSubscriptionId,
            pending: {
                value: params.value,
                currency: params.currency,
                transactionId: params.transactionId,
                organizationId: params.organizationId,
                quantity: params.quantity,
                plan: params.plan,
                email: params.email ?? undefined,
            },
            email: params.email,
        });
    });
}

/**
 * After Paddle `checkout.completed` + `subscription.create` (which writes `"temp"`),
 * wait for a real `sub_…` (overlay id or webhook-backed `subscription.get`) and
 * fire `purchase` once. Fire-and-forget: the caller must not await this before
 * redirecting. Never throws.
 */
export async function confirmPaidConversionFromCheckout(input: {
    checkoutData?: unknown;
    organizationId?: string;
    quantity?: number;
    plan?: string;
    email?: string | null;
    loadSubscription?: () => Promise<SubscriptionSnapshot | null | undefined>;
}): Promise<{ fired: boolean }> {
    try {
        const data = input.checkoutData as
            | {
                  transaction_id?: string | null;
                  currency_code?: string | null;
                  totals?: { total?: number | string | null } | null;
              }
            | undefined;
        const checkoutSubscriptionId = extractCheckoutSubscriptionId(input.checkoutData);
        const pending: PendingPaidConversion = {
            value: extractCheckoutValue(data),
            currency: data?.currency_code ?? undefined,
            transactionId: data?.transaction_id ?? undefined,
            organizationId: input.organizationId,
            quantity: input.quantity,
            plan: input.plan ?? extractCheckoutPlan(input.checkoutData),
            email: input.email ?? undefined,
            checkoutSubscriptionId,
        };
        const storage = typeof window === "undefined" ? null : sessionStorageOrNull();
        markPendingPaidConversion(storage, pending);

        const snapshot = await resolvePaidSubscriptionId({
            checkoutSubscriptionId,
            loadSubscription: input.loadSubscription,
            pollAttempts: input.loadSubscription ? PAID_CONVERSION_POLL_ATTEMPTS : 1,
            intervalMs: PAID_CONVERSION_POLL_INTERVAL_MS,
        });
        const fired = tryFirePaidConversion({
            outcome: classifyPaidSubscription(snapshot),
            paddleSubscriptionId: snapshot.paddleSubscriptionId,
            pending,
            email: input.email,
        });
        return { fired };
    } catch {
        return { fired: false };
    }
}

/**
 * After redirect / app shell load: if checkout left a pending conversion and
 * `subscription.get` now has a real `sub_…`, fire once. Never throws.
 */
export function flushPendingPaidConversion(input: {
    subscription?: SubscriptionSnapshot | null;
    email?: string | null;
    organizationId?: string | null;
}): void {
    afterSuccessTrack(() => {
        if (typeof window === "undefined") {
            return;
        }
        const storage = sessionStorageOrNull();
        const pending = peekPendingPaidConversion(storage);
        if (!pending) {
            return;
        }
        if (!pendingMatchesOrganization(pending, input.organizationId)) {
            return;
        }
        const snapshot: SubscriptionSnapshot = {
            paddleSubscriptionId: input.subscription?.paddleSubscriptionId ?? pending.checkoutSubscriptionId,
            isActive: input.subscription?.isActive,
            status: input.subscription?.status,
        };
        tryFirePaidConversion({
            outcome: classifyPaidSubscription(snapshot),
            paddleSubscriptionId: snapshot.paddleSubscriptionId,
            pending: {
                ...pending,
                organizationId: pending.organizationId ?? input.organizationId ?? undefined,
            },
            email: input.email ?? pending.email,
        });
    });
}

export { PURCHASE_EVENT };
